import { moneyFromDecimalString } from '@ibe/utils';
import { z } from 'zod';

const CODE_MIN = 2;
const CODE_MAX = 32;
const NAME_MAX = 255;
const CURRENCY_LENGTH = 3;
const DESCRIPTION_MAX = 4000;
const DEPARTMENT_MAX = 100;

const CODE_PATTERN = new RegExp(`^[A-Z0-9_-]{${String(CODE_MIN)},${String(CODE_MAX)}}$`, 'u');
const CURRENCY_PATTERN = /^[A-Z]{3}$/u;

const inclusionFormFields = {
  code: z
    .string()
    .trim()
    .toUpperCase()
    .min(CODE_MIN, `Code must be at least ${String(CODE_MIN)} characters`)
    .max(CODE_MAX, `Code must be ${String(CODE_MAX)} characters or fewer`)
    .regex(CODE_PATTERN, {
      message: 'Code can only contain uppercase letters, digits, underscores and hyphens',
    }),
  name: z
    .string()
    .trim()
    .min(1, 'A name is required')
    .max(NAME_MAX, `Keep the name under ${String(NAME_MAX)} characters`),
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .length(CURRENCY_LENGTH, 'Currency must be a 3-letter ISO 4217 code')
    .regex(CURRENCY_PATTERN, 'Currency must be an ISO 4217 code such as USD or AUD'),
  unitCost: z.string().trim().min(1, 'A unit cost is required'),
  description: z
    .string()
    .trim()
    .max(DESCRIPTION_MAX, `Keep the description under ${String(DESCRIPTION_MAX)} characters`)
    .nullish(),
  requiresFulfillment: z.boolean(),
  fulfillmentDepartment: z
    .string()
    .trim()
    .max(DEPARTMENT_MAX, `Keep the department under ${String(DEPARTMENT_MAX)} characters`)
    .nullish(),
  defaultInstructions: z
    .string()
    .trim()
    .max(DESCRIPTION_MAX, `Keep the instructions under ${String(DESCRIPTION_MAX)} characters`)
    .nullish(),
  isActive: z.boolean(),
};

export const inclusionRow = z.object({
  id: z.string(),
  code: z.string().optional(),
  name: z.string().optional(),
  currency: z.string().optional(),
  unitCost: z.string().optional(),
  description: z.string().nullable().optional(),
  requiresFulfillment: z.boolean().optional(),
  fulfillmentDepartment: z.string().nullable().optional(),
  defaultInstructions: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export const inclusionDetail = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  currency: z.string(),
  unitCost: z.string(),
  description: z.string().nullable(),
  requiresFulfillment: z.boolean(),
  fulfillmentDepartment: z.string().nullable(),
  defaultInstructions: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number(),
});

export const inclusionFormInput = z.object(inclusionFormFields).superRefine((value, ctx) => {
  try {
    moneyFromDecimalString(value.unitCost, value.currency);
  } catch (error) {
    ctx.addIssue({
      code: 'custom',
      message: error instanceof Error ? error.message : 'Invalid unit cost',
      path: ['unitCost'],
    });
  }
});

export type InclusionRow = z.infer<typeof inclusionRow>;
export type InclusionDetail = z.infer<typeof inclusionDetail>;
export type InclusionFormInput = z.infer<typeof inclusionFormInput>;
